import { useState, useEffect, useCallback } from "react";
import {
  getQueuesDashboard,
  getWorkersStatus,
  getLastRunTimes,
  getWaitingJobs,
  getDelayedJobs,
  getActiveJobs,
  getCompletedJobs,
  getFailedJobs,
  pauseWorker,
  resumeWorker,
  JobStatus,
  QueueCounts,
  WorkerStatus,
  JobsListResponse,
  JOBS_PAGE_SIZE,
} from "api/Reviews";
import { REFRESH_INTERVAL } from "../config";

const defaultJobStatus: JobStatus = {
  waiting: 0,
  delayed: 0,
  active: 0,
  completed: 0,
  failed: 0,
};

export type JobsTabStatusKey =
  | "waiting"
  | "delayed"
  | "active"
  | "completed"
  | "failed";

export const useJobsData = () => {
  const [jobStatus, setJobStatus] = useState<JobStatus>(defaultJobStatus);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRunTimes, setLastRunTimes] = useState<Record<string, string>>({});
  const [queues, setQueues] = useState<Record<string, QueueCounts>>({});
  const [workers, setWorkers] = useState<WorkerStatus[]>([]);
  const [workersError, setWorkersError] = useState<string | null>(null);

  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    setWorkersError(null);

    try {
      const [queuesData, workersData, lastRunTimesData] =
        await Promise.all([
          getQueuesDashboard(),
          getWorkersStatus(),
          getLastRunTimes(),
        ]);

      setQueues(queuesData.queues || {});
      setWorkers(workersData.workers || []);
      if (workersData.error) setWorkersError(workersData.error);
      setLastRunTimes(lastRunTimesData || {});
      const q = queuesData.queues || {};
      setJobStatus({
        waiting: Object.values(q).reduce((s, c) => s + (c.waiting ?? 0), 0),
        delayed: Object.values(q).reduce((s, c) => s + (c.delayed ?? 0), 0),
        active: Object.values(q).reduce((s, c) => s + (c.active ?? 0), 0),
        completed: Object.values(q).reduce((s, c) => s + (c.completed ?? 0), 0),
        failed: Object.values(q).reduce((s, c) => s + (c.failed ?? 0), 0),
      });
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to fetch jobs data");
      setJobStatus(defaultJobStatus);
      setLastRunTimes({});
      setQueues({});
      setWorkers([]);
    }

    if (showLoading) setLoading(false);
  }, []);

  const fetchJobsList = useCallback(
    async (statusKey: JobsTabStatusKey, page = 1): Promise<JobsListResponse> => {
      const limit = JOBS_PAGE_SIZE;
      switch (statusKey) {
        case "waiting":
          return getWaitingJobs(page, limit);
        case "delayed":
          return getDelayedJobs(page, limit);
        case "active":
          return getActiveJobs(page, limit);
        case "completed":
          return getCompletedJobs(page, limit);
        case "failed":
          return getFailedJobs(page, limit);
        default:
          return { jobs: [], total: 0 };
      }
    },
    []
  );

  const handlePauseWorker = useCallback(async (name: string) => {
    const res = await pauseWorker(name);
    if (res.error) setWorkersError(res.error);
    else await fetchData(false);
  }, [fetchData]);

  const handleResumeWorker = useCallback(async (name: string) => {
    const res = await resumeWorker(name);
    if (res.error) setWorkersError(res.error);
    else await fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchData(false), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  return {
    jobStatus,
    loading,
    error,
    lastUpdate,
    autoRefresh,
    setAutoRefresh,
    lastRunTimes,
    fetchData,
    fetchJobsList,
    queues,
    workers,
    workersError,
    handlePauseWorker,
    handleResumeWorker,
  };
};
